import React, { useEffect, useRef } from "react";

/**
 * Lightweight animated particle field for the hero background.
 * Uses <canvas> for smoothness. Reads --primary from CSS so it re-tints per theme.
 * Pauses when the tab is hidden and when prefers-reduced-motion is on.
 */
export function ParticleField({ density = 60, className = "" }) {
    const canvasRef = useRef(null);
    const rafRef = useRef();
    const runningRef = useRef(true);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        let particles = [];

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const { clientWidth, clientHeight } = canvas.parentElement;
            canvas.width = clientWidth * dpr;
            canvas.height = clientHeight * dpr;
            canvas.style.width = `${clientWidth}px`;
            canvas.style.height = `${clientHeight}px`;
            ctx.scale(dpr, dpr);
        };

        const readPrimaryHsl = () => {
            const raw = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
            // Fallback to teal-ish
            return raw || "168 90% 45%";
        };

        const build = () => {
            const { clientWidth: w, clientHeight: h } = canvas.parentElement;
            particles = new Array(density).fill(0).map(() => ({
                x: Math.random() * w,
                y: Math.random() * h,
                r: Math.random() * 1.6 + 0.4,
                vx: (Math.random() - 0.5) * 0.15,
                vy: (Math.random() - 0.5) * 0.15,
                a: Math.random() * 0.6 + 0.2,
                twinkle: Math.random() * Math.PI * 2,
            }));
        };

        const draw = (t) => {
            if (!runningRef.current) return;
            const { clientWidth: w, clientHeight: h } = canvas.parentElement;
            ctx.clearRect(0, 0, w, h);
            const hsl = readPrimaryHsl();

            // Soft glow blobs
            for (const p of particles) {
                p.x += p.vx;
                p.y += p.vy;
                p.twinkle += 0.03;
                if (p.x < -20) p.x = w + 20;
                if (p.x > w + 20) p.x = -20;
                if (p.y < -20) p.y = h + 20;
                if (p.y > h + 20) p.y = -20;
                const alpha = p.a * (0.6 + 0.4 * Math.sin(p.twinkle));
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `hsl(${hsl} / ${alpha})`;
                ctx.shadowBlur = 12;
                ctx.shadowColor = `hsl(${hsl} / ${alpha * 0.8})`;
                ctx.fill();
            }
            rafRef.current = requestAnimationFrame(draw);
        };

        resize();
        build();
        if (!prefersReduced) rafRef.current = requestAnimationFrame(draw);

        const onResize = () => { resize(); build(); };
        window.addEventListener("resize", onResize);
        const onVis = () => {
            runningRef.current = !document.hidden;
            if (!document.hidden && !prefersReduced) rafRef.current = requestAnimationFrame(draw);
        };
        document.addEventListener("visibilitychange", onVis);

        return () => {
            cancelAnimationFrame(rafRef.current);
            window.removeEventListener("resize", onResize);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [density]);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden
            className={`pointer-events-none absolute inset-0 w-full h-full ${className}`}
            data-testid="particle-field"
        />
    );
}
